import { useContext, useMemo } from 'react';
import { mergeAsyncState, Status } from '../shared/hooks/asyncState';
import useQuery from './internal/useQuery';
import useVisualizeJourney from './internal/useVisualizeJourney';
import { gate } from 'hub-http/gates';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
const AutoCurrencyFormatFixGate = gate('ReportMigration:JourneySurfacingDealAmount');
const updateCurrencyProperty = properties => {
  const newProperties = JSON.parse(JSON.stringify(properties));
  Object.keys(newProperties).forEach(propertyKey => {
    const property = newProperties[propertyKey];

    // If property.name.format.type is 'currency', set property.references = {}
    if (property.format && property.format.type === 'currency') {
      property.references = {}; // Set references to an empty object
    }

    // If property.format.companyCurrencyCode exists, copy its value to property.format.currencyCode
    if (property.format && property.format.companyCurrencyCode) {
      property.format.currencyCode = property.format.companyCurrencyCode; // Copy the value
    }
  });
  return newProperties;
};
function fixDatamap(datamap) {
  if (!datamap || !datamap.primary || !datamap.primary.properties) {
    return undefined;
  }
  return Object.assign({}, datamap, {
    primary: Object.assign({}, datamap.primary, {
      properties: updateCurrencyProperty(datamap.primary.properties)
    })
  }, datamap.summary ? {
    summary: Object.assign({}, datamap.summary, {
      properties: updateCurrencyProperty(datamap.summary.properties)
    })
  } : {});
}

/**
 * All in one hook to resolve a Journey Report.
 * @param report The Journey Report object
 */
export const useJourneyReport = (report, reportOptions, {
  onDrilldown
} = {}, debug) => {
  const {
    gates
  } = useContext(UserInfoContext);

  /**
   * Generate and memoize the object containing the query and (TODO) report
   * options.
   */
  const queryWithOpts = useMemo(() => ({
    journeyQuery: report.journeyQuery,
    reportOptions
  }), [report, reportOptions]);
  const resolveParameters = useMemo(() => ({
    reportId: report.id
  }), [report]);

  /**
   * Performs asynchronous polling for report, manages when to refetch report
   * data for an updated query.
   */
  const queryState = useQuery(queryWithOpts, resolveParameters, debug);
  const datamap = queryState.status === Status.SUCCEEDED ? queryState.data : undefined;

  // fix datamap
  const fixedDatamap = useMemo(() => {
    const applyFixAutoCurrencyFormat = gates === null || gates === void 0 ? void 0 : gates.includes(AutoCurrencyFormatFixGate);
    return applyFixAutoCurrencyFormat ? fixDatamap(datamap) : datamap;
  }, [datamap, gates]);

  /**
   * Constructs a visualization from the datamap.
   */
  const useVisualizeJourneyState = useVisualizeJourney(report, reportOptions, fixedDatamap, onDrilldown);
  const visualization = useVisualizeJourneyState.status === Status.SUCCEEDED ? useVisualizeJourneyState.data : undefined;
  /**
   * TODO: Implement the "lifecycle functions" of the report.
   */

  const {
    loading,
    settled,
    error
  } = mergeAsyncState([queryState
  //constructDatasetState,
  ]);
  return {
    visualization,
    datamap: fixedDatamap,
    settled,
    loading,
    error,
    report
  };
};